import * as Sentry from '@sentry/nextjs';
import 'client-only';

import { SIGNING_FLOW_TAG_KEY, SIGNING_FLOW_TAG_VALUE } from './constants';

/**
 * Mark the active Sentry scope (browser pageview) as a signing-flow
 * session. Returns a cleanup function that removes the marker — used by
 * the React hook on unmount so the marker does not leak onto unrelated
 * errors after SPA navigation away from a signing surface.
 *
 * Never throws. Sentry errors are swallowed so observability can never
 * interrupt the signing flow. When Sentry is disabled, `getCurrentScope()`
 * returns a no-op scope whose `setTag` does nothing.
 */
export function observeSigningFlow(): () => void {
  try {
    Sentry.getCurrentScope().setTag(
      SIGNING_FLOW_TAG_KEY,
      SIGNING_FLOW_TAG_VALUE
    );
    // Scope tags propagate to error events but NOT to span attributes
    // in the EAP Spans dataset. Mirror the tag onto the active root
    // span (pageload/navigation) so failure_rate() widgets can filter
    // by `portal.signing_flow`. Best-effort: if the root span has
    // already ended (slow pageload + late mount), this is a no-op.
    const active = Sentry.getActiveSpan();
    if (active) {
      Sentry.getRootSpan(active).setAttribute(
        SIGNING_FLOW_TAG_KEY,
        SIGNING_FLOW_TAG_VALUE
      );
    }
  } catch {
    // Observability must never interrupt the signing flow.
  }
  return () => {
    try {
      Sentry.getCurrentScope().setTag(SIGNING_FLOW_TAG_KEY, undefined);
    } catch {
      // Observability must never interrupt the signing flow.
    }
  };
}
